import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _process$env$NODE_ENV, _ShowHideResouceKey;
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { ImLocalizationService } from '@im/localization';
import { localizationContext } from './localizationContext';
import { ShowHideResouceKey } from './editTranslate/showHideResouceKey';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var IMO_CULTURE_COOKIE = 'ENDEAVOUR_USER_CULTURE_COOKIE';
var IMO_CURRENCY_COOKIE = 'ResellerCurrency';
var XVANTAGE_CULTURE_COOKIE = 'XVANTAGE_CULTURE_COOKIE';
var XVANTAGE_CURRENCY_COOKIE = 'XVANTAGE_CURRENCY_COOKIE';
var isEditingEnabled = ((_process$env$NODE_ENV = process.env.NODE_ENV) == null ? void 0 : _process$env$NODE_ENV.startsWith('staging')) === true && (localStorage.getItem('enableTranslationEditing') || 'false') === 'true';
export function LocalizationProvider(props) {
  var apiHeaders = props.apiHeaders,
    appId = props.appId,
    children = props.children,
    defaultLocale = props.defaultLocale,
    preloadResources = props.preloadResources,
    showLogs = props.showLogs,
    supportedLocales = props.supportedLocales,
    url = props.url,
    useApi = props.useApi;

  //differentiate cookies between apps with the same origin
  var suffix = appId === 'cep' ? '' : "_".concat(appId.toUpperCase());
  var savedLocale = Cookies.get(XVANTAGE_CULTURE_COOKIE + suffix) || '';
  var savedCurrency = Cookies.get(XVANTAGE_CURRENCY_COOKIE + suffix);
  var _useState = useState(savedLocale),
    _useState2 = _slicedToArray(_useState, 2),
    locale = _useState2[0],
    setLocaleState = _useState2[1];
  var _useState3 = useState(savedCurrency),
    _useState4 = _slicedToArray(_useState3, 2),
    currencyCode = _useState4[0],
    setCurrencyCodeState = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showKey = _useState6[0],
    setShowKey = _useState6[1];
  var _useState7 = useState(undefined),
    _useState8 = _slicedToArray(_useState7, 2),
    context = _useState8[0],
    setContext = _useState8[1];
  var setLocale = useCallback(function (newLocale) {
    setLocaleState(newLocale);
    Cookies.set(IMO_CULTURE_COOKIE + suffix, newLocale);
    Cookies.set(XVANTAGE_CULTURE_COOKIE + suffix, newLocale);
  }, [setLocaleState, suffix]);
  useEffect(function () {
    if (!defaultLocale || supportedLocales.length === 0) {
      return;
    }
    if (!supportedLocales.includes(locale)) {
      setLocale(defaultLocale);
    }
  }, [locale, defaultLocale, supportedLocales, setLocale]);
  var localizationService = useMemo(function () {
    if (!url || !locale) {
      return;
    }
    var appIdMap = {
      cep: 'IMCEP',
      x4a: 'IMX4A',
      x4v: 'IMX4V'
    };
    return new ImLocalizationService({
      currencyCode: currencyCode,
      fallbackLocale: 'en-us',
      preloadResources: preloadResources,
      resourceLoader: {
        appName: appIdMap[appId],
        // future maybe: buildVars.APP_NAME
        headers: apiHeaders,
        isEditingEnabled: isEditingEnabled,
        showLogs: showLogs,
        url: url,
        useApi: useApi
      },
      useLocale: locale
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, url]);
  //creating new instance of ImLocalizationService is an expensive operation
  //and should depend only on locale and url.

  var setCurrencyCode = useCallback(function (newCode) {
    if (localizationService) localizationService.updateCurrency(newCode);
    Cookies.set(IMO_CURRENCY_COOKIE + suffix, newCode);
    Cookies.set(XVANTAGE_CURRENCY_COOKIE + suffix, newCode);
    setCurrencyCodeState(newCode);
  }, [localizationService, suffix]);
  var showHideKey = useCallback(function (show) {
    setShowKey(show);
  }, []);
  /**
   * resourcesLoadedMap
   */
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    resourcesCount = _useState10[0],
    setResourcesCount = _useState10[1];
  var resourcesLoadedMapRef = useRef({});

  // clear resourcesLoadedMap when locale changed
  useEffect(function () {
    resourcesLoadedMapRef.current = {};
  }, [locale]);
  var resourceIsLoaded = useCallback(function (resource) {
    return resourcesLoadedMapRef.current[resource];
  }, []);
  var resourceSetIsLoaded = useCallback(function (resource, loaded) {
    var prevState = resourcesLoadedMapRef.current[resource];
    if (prevState === undefined || loaded) {
      resourcesLoadedMapRef.current[resource] = loaded;
    }
    if (loaded && prevState !== loaded) {
      setResourcesCount(function (c) {
        return c + 1;
      });
      // console.log('resourceSetIsLoaded', resource);
    }
  }, []);
  var _useState11 = useState(0),
    _useState12 = _slicedToArray(_useState11, 2),
    resourcesDelayedCount = _useState12[0],
    setResourcesDelayedCount = _useState12[1];
  useEffect(function () {
    var isActive = true;
    var id = setTimeout(function () {
      if (isActive) {
        setResourcesDelayedCount(resourcesCount);
      }
    }, 200);
    return function () {
      isActive = false;
      clearTimeout(id);
    };
  }, [resourcesCount, setResourcesDelayedCount]);
  useEffect(function () {
    if (isEditingEnabled) {
      window.addEventListener('contextmenu', function (e) {
        return e.preventDefault();
      });
      var handleRightClickEvent = function handleRightClickEvent(evt) {
        if (evt.which === 3 && evt.detail === 2) {
          evt.preventDefault();
          if (context) setShowKey(!context.isShowKeyEnabled);
        }
      };
      window.addEventListener('mouseup', handleRightClickEvent);
      return function () {
        window.removeEventListener('mouseup', handleRightClickEvent);
      };
    }
  }, [context, setShowKey]);

  /**
   * create context
   */
  useEffect(function () {
    if (!localizationService) {
      return;
    }
    setContext({
      isEditingEnabled: isEditingEnabled,
      isShowKeyEnabled: showKey,
      locale: locale,
      localizationService: localizationService,
      resourceIsLoaded: resourceIsLoaded,
      resourceSetIsLoaded: resourceSetIsLoaded,
      resourcesCount: resourcesDelayedCount,
      setCurrencyCode: setCurrencyCode,
      setLocale: setLocale,
      showHideKey: showHideKey,
      showLogs: showLogs,
      supportedLocales: supportedLocales
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localizationService, currencyCode, resourcesDelayedCount, showKey]);
  //If locale changes, then localizationService would also change
  //so dependency on locale is not required.

  if (!context) {
    return null;
  }
  var Provider = localizationContext.Provider;
  return /*#__PURE__*/_jsxs(Provider, {
    value: context,
    children: [children, isEditingEnabled && (_ShowHideResouceKey || (_ShowHideResouceKey = /*#__PURE__*/_jsx(ShowHideResouceKey, {})))]
  });
}